import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import {map, share, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

import {
  Merchant,
  Price,
  ProductModifier,
  ModifierGroup,
  ProductVariation,
  RestaurantAvailableStatus,
  RestaurantCategory,
  RestaurantProduct,
  RestaurantProductInfo,
  Taxes, WorkTime, CatalogData, Group, DailyAvailability, DailyAvailabilityResponse, Restaurant
} from '../models/restaurant.model';
// services
import {RouteService} from './route.service';
import {forkJoin} from 'rxjs';
import {
  dataToGroup,
  dataToModifiers,
  dataToRestaurant,
  dataToRestaurantAvailableStatus, dataToRestaurants, faker, modifierSoldOut
} from '../dto/restaurant.dto';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {OrderOnlyModalComponent} from '../../shared/components/order-only-modal/order-only-modal.component';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
// models

@Injectable()
export class RestaurantService {
  private noCacheHttpOptions = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Content-Type':  'application/json'
    })
  };

  constructor(
    private http: HttpClient,
    private routeService: RouteService,
    private router: Router,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver
  ) {
  }

  public getMerchantById(merchantId: string): Observable<Merchant> {
    const apiURL = environment.apiUrl;
    return this.http.get<Merchant>(`${apiURL}merchants/${merchantId}`);
  }

  public getGroup(baseUrl: string): Observable<Group> {
    const apiURL = environment.apiUrl + baseUrl;
    return this.http.get(`${apiURL}`)
      .pipe(map( (response: any) => dataToGroup(response)));
  }

  public getRestaurants(baseUrl): Observable<any> {
    const apiURL = environment.apiUrl + baseUrl;
    return this.http.get(`${apiURL}/venues/active`)
      .pipe(
        map((restaurantsResponse: any): any => {
          return restaurantsResponse.map((restaurant) => {
            if (restaurant?.kyoo_location?.contact_phone?.includes('000000')) {
              restaurant.kyoo_location.contact_phone = null;
            }
            return dataToRestaurant(restaurant);
          });
        }));
  }

  public getRestaurantCatalogData(locationId: string, isCatalogUploaded: boolean, merchantId?: string): Observable<CatalogData> {
    let apiUrl = '';
    let httpOptions = {};
    if (isCatalogUploaded && merchantId) {
      apiUrl = `${environment.catalogApiUrl}merchants/${merchantId}/locations/${locationId}/catalog-data.json?${faker(5)}`;
      httpOptions = this.noCacheHttpOptions;
    } else if (isCatalogUploaded) {
      apiUrl = `${environment.catalogApiUrl + this.routeService.getBaseUrl()}/locations/${locationId}/catalog-data.json?${faker(5)}`;
      httpOptions = this.noCacheHttpOptions;
    } else if (merchantId) {
      apiUrl = `${environment.apiUrl}merchants/${merchantId}/locations/${locationId}/catalog/catalog-data`;
    } else {
      apiUrl = `${environment.apiUrl + this.routeService.getBaseUrl()}/locations/${locationId}/catalog/catalog-data`;
    }
    return this.http.get(apiUrl, httpOptions)
      .pipe(
        map((response: any) => {
          const data = response;
          data.categories = response.categories.map(category => {
            return new RestaurantCategory(
              category.id,
              category.category_data.name,
              category.category_data.before_opening,
              category.category_data.before_closing,
              category.category_data.before_pickup,
              category.category_data.lead_times_enabled,
              category.category_data.allow_custom_note,
              this.getDailyAvailability(category.category_data.daily_availability_list),
              category.category_data.fulfillments,
              category.category_data.available_from ? new Date(category.category_data.available_from) : new Date(),
              category.category_data.available_until ? new Date(category.category_data.available_until) : new Date(),
              category.category_data.daily_availability,
              category.category_data.restrict_fulfillments,
              category.category_data.start_end_dates_enabled,
              category.category_data.inventory_enabled ?? false,
              locationId,
              false,
              category.category_data.parent_category_id,
            );
          });
          this.getExtraTaxes(locationId).pipe(
            switchMap(extraTaxes => {
              if (data.taxes.length) {
                data.taxes = response.taxes.map(tax => {
                  let percentage = tax.tax_data.percentage * 0.01;
                  let isInclusive = false;

                  if (tax.tax_data.inclusion_type !== 'ADDITIVE') {
                    percentage = 0;
                    isInclusive = true;
                  }
                  return new Taxes(tax.id, percentage, extraTaxes.kyooFee, extraTaxes.autoGratuity, isInclusive);
                });
              } else {
                return new Array(new Taxes(null, null, extraTaxes.kyooFee, extraTaxes.autoGratuity, false));
              }
            }));
          const modifierGroups: ModifierGroup[] = [];
          const modifierList = data.modifiers;
          for (const group in modifierList) {
            if (group) {
              const modifierGroup = modifierList[group];
              modifierGroups.push(this.convertDataToModifierGroup(modifierGroup, locationId));
            }
          }
          data.modifiers = modifierGroups;
          return data;
        }),
        share()
      );
  }

  public getCategoryProducts(locationId: string, categoryId: string, modifiers: ModifierGroup[], isCatalogUploaded: boolean, category: RestaurantCategory, merchantId?: string): Observable<RestaurantProduct[]> {
    let apiUrl = '';
    let httpOptions = {};
    if (isCatalogUploaded && merchantId) {
      apiUrl = `${environment.catalogApiUrl}merchants/${merchantId}/locations/${locationId}/categories/${categoryId}/items.json`;
      httpOptions = this.noCacheHttpOptions;
    } else if (isCatalogUploaded) {
      apiUrl = `${environment.catalogApiUrl + this.routeService.getBaseUrl()}/locations/${locationId}/categories/${categoryId}/items.json`;
      httpOptions = this.noCacheHttpOptions;
    } else if (merchantId) {
      apiUrl = `${environment.apiUrl}merchants/${merchantId}/locations/${locationId}/catalog/categories/${categoryId}/items`;
    } else {
      apiUrl = `${environment.apiUrl + this.routeService.getBaseUrl()}/locations/${locationId}/catalog/categories/${categoryId}/items`;
    }
    return this.http.get(apiUrl, httpOptions)
      .pipe(map((items: any) => {
        return this.convertDataToRestaurantProducts(locationId, items, modifiers, category);
      }));
  }

  public getAllCategoryProducts(locationId: string, modifiers: ModifierGroup[], isCatalogUploaded: boolean, merchantId: string): Observable<RestaurantProduct[]> {
    let apiUrl = `${environment.catalogApiUrl}merchants/${merchantId}/locations/${locationId}/categories/allitems.json`;
    let httpOptions = this.noCacheHttpOptions;

    return this.http.get(apiUrl, httpOptions)
      .pipe(
        map((items: any[]) => {
          return items.map(item => {
            return this.convertDataToRestaurantProducts(locationId, item.items, modifiers, item.category);
          });
        }),
        map((groupedProducts: RestaurantProduct[][]) => {
          return groupedProducts.flat();
        })
      );
  }

  public getRestaurantInventoryData(locationId: string, variationIds: string[], merchantId?: string): Observable<any> {
    let apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    if (merchantId) {
      apiURL = `${environment.apiUrl}merchants/${merchantId}`;
    }
    const body = {variation_ids: variationIds};
    return this.http.post(`${apiURL}/locations/${locationId}/catalog/inventory`, body)
      .pipe(map((items: any) => {
        return items;
      }));
  }

  private getLocationById(locationId: string): Observable<object> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}venues/${locationId}`);
  }

  public getProductsByReward(rewardId: string, locationId: string, merchantId: string): Observable<any> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/loyalty/rewards/${rewardId}/locations/${locationId}/items`);
  }

  private getExtraTaxes(locationId: string): Observable<{ kyooFee: number, autoGratuity: number }> {
    return this.getLocationById(locationId).pipe(
      map(response => {
        const taxes = response['venue'].kyoo_location;
        return {
          kyooFee: taxes.default_kyoo_fee,
          autoGratuity: taxes.default_auto_gratuity_percent
        };
      })
    );
  }

  private convertDataToModifierGroup(modifierGroup, locationId: string): ModifierGroup {
    return new ModifierGroup(
      modifierGroup.id,
      modifierGroup.modifier_list_data.name,
      modifierGroup.modifier_list_data.modifiers
        .map(modifier => {
          // tslint:disable-next-line:no-unused-expression
          modifier.modifier_data.price_money ? modifier.modifier_data.price_money : modifier.modifier_data.price_money = {amount: 0};
          return new ProductModifier(
            modifier.id,
            modifier.modifier_data.name,
            new Price(
              modifier.modifier_data.price_money.amount,
              modifier.modifier_data.price_money.currency
            ),
          true,
          false,
            false,
            false,
          modifier.modifier_data.location_overrides ? modifierSoldOut(modifier.modifier_data.location_overrides, locationId) : null
          );
        }),
      modifierGroup.modifier_list_data.selection_type
    );
  }

  private convertDataToRestaurantProducts(locationId: string, products, modifierGroups: ModifierGroup[], category: RestaurantCategory): RestaurantProduct[] {
    return products.map(product => {
      return this.convertDataToRestaurantProduct(locationId, product, modifierGroups, category);
    });
  }

  public convertDataToRestaurantProduct(locationId: string, product, modifierGroups: ModifierGroup[], category: RestaurantCategory): RestaurantProduct {
    return new RestaurantProduct(
      new RestaurantProductInfo(product.id,
        product.item_data.name,
        product.item_data.image_url,
        product.item_data.description,
        `#${product.item_data.label_color}`,
        product.item_data.tax_ids || [],
      ),
      this.dataToVariations(product.item_data.variations, locationId, category),
      this.getModifiers(product, modifierGroups),
      category.id,
      locationId,
      false,
      category
    );
  }

  public checkIsCatalogUploaded(merchantId: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}merchants/${merchantId}/catalog/check-uploaded`);
  }


  public checkQROrderOnlyOrder(restaurant: Restaurant): void {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const queryParams = { ...urlTree.queryParams };
    if (restaurant.qrOrderOnly && !queryParams['sharedOrderId'] && !queryParams['sharedOwnerId']) {
      if (queryParams['qrCode'] !== undefined) {
        delete queryParams['qrCode'];
        this.router.navigate([], {
          queryParams: queryParams,
          replaceUrl: true,
        });
      } else {
        this.openQrOrderModal(restaurant);
      }
    }
  }

  private openQrOrderModal(restaurant: Restaurant): void {
    const isMobile = this.breakpointObserver.isMatched(Breakpoints.Handset);
    this.dialog.open(OrderOnlyModalComponent, {
      data: {
        restaurant
      },
      height: isMobile ? '40vh' : '500px',
      width: isMobile ? '100vw' : '600px',
      disableClose: true
    });
  }

  private getQuantity(userData: string): number {
    const quantity = Number(userData);
    return Number.isNaN(quantity) ? null : quantity;
  }

  private getDailyAvailability(data: DailyAvailabilityResponse[]): DailyAvailability[] {
    if (!data) {
      return null;
    }
    const dailyAvailability: DailyAvailability[] = [];
    for (const day of data) {
      dailyAvailability.push(new DailyAvailability(day.enabled, day.value, this.convertTimeRangeToDate(day.start_time, true), this.convertTimeRangeToDate(day.end_time, false)));
    }
    return dailyAvailability;
  }

  private getSoldOut(locationId: string, variation: any): boolean {
    const locationOverrides = variation.item_variation_data.location_overrides;
    if (locationOverrides == null || locationOverrides.length === 0) {
      return false;
    }
    for (const locationOverride of locationOverrides) {
      if (locationOverride.location_id === locationId) {
        return locationOverride.sold_out ?? false;
      }
    }
  }

  private getTrackInventory(locationId: string, variation: any): boolean {
    const locationOverrides = variation.item_variation_data.location_overrides;
    if (locationOverrides == null || locationOverrides.length === 0) {
      return false;
    }
    for (const locationOverride of locationOverrides) {
      if (locationOverride.location_id === locationId) {
        return locationOverride.track_inventory ?? false;
      }
    }
  }

  private convertTimeRangeToDate(range: WorkTime, isStartTime: boolean): Date {
    const date = new Date();
    if (!range) {
      if (isStartTime) {
        date.setHours(0);
        date.setMinutes(0);
      }
      if (!isStartTime) {
        date.setHours(23);
        date.setMinutes(59);
      }
    } else {
      const meridian = range.meridian ? 0 : 12;
      const hour = range.time.hour === 12 ? 0 : range.time.hour;
      date.setMinutes(range.time.minute);
      date.setHours(hour + meridian);
    }
    return date;
  }

  private getModifiers(item, modifierGroups) {
    let modifierList = [];
    const modifiers = [];

    if (item.item_data.modifier_list_info) {
      modifierList = item.item_data.modifier_list_info.filter(modifier_list => modifier_list.enabled !== false);
    }

    modifierList.forEach((modifier) => {
      const foundModifierGroup = modifierGroups.find((modifierGroupItem) => modifierGroupItem.id === modifier.modifier_list_id);

      if (foundModifierGroup) {
        const targetModifierGroup = JSON.parse(JSON.stringify(foundModifierGroup));

        if (modifier && modifier.modifier_overrides && modifier.modifier_overrides.length) {
          targetModifierGroup?.modifiers.forEach(targetModifier => {
            const override = modifier.modifier_overrides.find(overrides => overrides.modifier_id === targetModifier.id);

            if (override) {
              targetModifier.onByDefault = override.on_by_default;
              targetModifier.isSelected = override.on_by_default;
            }

            if (modifier.hidden_from_customer) {
              targetModifier.hiddenFromCustomer = true;
            }
          });
        }

        targetModifierGroup.minSelectedModifiers = modifier.min_selected_modifiers;
        targetModifierGroup.maxSelectedModifiers = modifier.max_selected_modifiers;
        targetModifierGroup.defaultMinSelectedModifiers = modifier.min_selected_modifiers;
        targetModifierGroup.defaultMaxSelectedModifiers = modifier.max_selected_modifiers;
        targetModifierGroup.hiddenFromCustomer = modifier.hidden_from_customer;
        modifiers.push(targetModifierGroup);
      }
    });

    return modifiers;
  }

  private createProductVariation(variation: any, locationId: string, price: any, category: RestaurantCategory): ProductVariation {
    const productVariation = new ProductVariation(
      variation.id,
      variation.item_variation_data.name,
      price,
      this.getQuantity(variation.item_variation_data.user_data),
      variation.item_variation_data.opening_lead_time,
      variation.item_variation_data.closing_lead_time,
      variation.item_variation_data.pickup_lead_time,
      variation.item_variation_data.lead_times_enabled,
      variation.item_variation_data.allow_custom_note,
      this.getDailyAvailability(variation.item_variation_data.daily_availability),
      variation.item_variation_data.fulfillments,
      variation.item_variation_data.available_from ? new Date(variation.item_variation_data.available_from) : new Date(),
      variation.item_variation_data.available_until ? new Date(variation.item_variation_data.available_until) : new Date(),
      variation.item_variation_data.daily_restriction_enabled,
      variation.item_variation_data.fulfillment_restriction_enabled,
      variation.item_variation_data.start_end_dates_enabled,
      variation.item_variation_data.use_inventory ?? false,
      this.getTrackInventory(locationId, variation),
      false,
      this.getSoldOut(locationId, variation),
      variation.item_variation_data.modifiers_disabled,
      variation.item_variation_data.modifier_lists ? dataToModifiers(variation.item_variation_data.modifier_lists) : []
    );
    this.checkProductVariationFields(productVariation, category);
    productVariation.nutrition_facts_enabled  = variation.item_variation_data.nutrition_facts_enabled;
    productVariation.nutrition_facts  = variation.item_variation_data.nutrition_facts;
    productVariation.dietary_labels_enabled  = variation.item_variation_data.dietary_labels_enabled;
    productVariation.dietary_labels  = variation.item_variation_data.dietary_labels;

    return productVariation;
  }

  private checkProductVariationFields(variation: ProductVariation, category: RestaurantCategory) {
    if (variation.useInventory == null && category.useInventory) {
      variation.useInventory = true;
      variation.trackInventory = true;
    }
    if (variation.allowCustomNote === null && category.allowCustomNote) {
      variation.allowCustomNote = true;
    }
    if (variation.isStartEndDatesEnabled == null && category.isStartEndDatesEnabled) {
      variation.isStartEndDatesEnabled = true;
      variation.availableFrom = category.availableFrom;
      variation.availableUntil = category.availableUntil;
    }
    if (variation.isDailyRestrictionEnabled == null && category.isDailyRestrictionEnabled) {
      variation.isDailyRestrictionEnabled = true;
      variation.dailyAvailability = category.dailyAvailability;
    }
    if (variation.isFulfillmentRestrictionEnabled == null && category.isFulfillmentRestrictionEnabled) {
      variation.isFulfillmentRestrictionEnabled = true;
      variation.availableInFulfillments = category.availableInFulfillments;
    }
    if (variation.isLeadTimesEnabled == null && category.isLeadTimesEnabled) {
      variation.isLeadTimesEnabled = true;
      variation.openingLeadTime = category.openingLeadTime;
      variation.closingLeadTime = category.closingLeadTime;
      variation.pickupLeadTime = category.pickupLeadTime;
    }
  }
  private dataToVariations(product: any, locationId: string, category: RestaurantCategory): ProductVariation[] {
    return product.map(variation => {
      const price: Price = new Price(
        this.getPriceAmount(variation, locationId),
        variation.item_variation_data.price_money ? variation.item_variation_data.price_money.currency : 'USD',
      );
      if (variation.item_variation_data.location_overrides) {
        const priceOverride: any[] = variation.item_variation_data.location_overrides.filter(override =>
          override.location_id === locationId &&
          override.price_money != null &&
          override.price_money.amount > 0
        );
        if (priceOverride.length > 0 && priceOverride[0] != null) {
          price.amount = priceOverride[0].price_money.amount;
        }
      }
      return this.createProductVariation(variation, locationId, price, category);
    });
  }

  private getPriceAmount(variation: any, locationId: string): number {
    let price = variation.item_variation_data.price_money ? variation.item_variation_data.price_money.amount : 0;
    if (variation.item_variation_data?.location_overrides) {
      variation.item_variation_data.location_overrides.map(override => {
        if (override?.pricing_type === 'FIXED_PRICING' && locationId === override?.location_id && override?.price_money != null) {
          price = override?.price_money?.amount;
        }
      });
    }
    return price;
  }
}
